// src/components/ml/EngineInsightsPanel.js
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (AI Intelligence Center · engines) — NEW
// "AI Engines" tab: every FlowSense AI engine with its live state, and full panels for
//   • SLA AI grading & escalation  — live risk, compliance, grade mix, decisions trend, hot jobs,
//                                    technician reliability scoreboard, weights & thresholds
//   • Road damage vision           — photos and labels, technician agreement per detector,
//                                    labels per damage class, next retrain progress, training runs
//   • Road condition & RUL         — roads scored, PCI bands, worst roads, treatments, road RUL
// Data: GET /api/v1/ml-ops/engines/:key (refreshed every 60 s while the tab is visible).
// Missing data shows "—" and says why; nothing is estimated.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, Grid, GridItem, Heading, HStack, Icon, IconButton, Link,
  Progress, SimpleGrid, Skeleton, Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack, Wrap, WrapItem,
  useColorModeValue, useToken,
} from '@chakra-ui/react';
import {
  MdAddRoad, MdCameraAlt, MdGavel, MdInsights, MdOpenInNew, MdRefresh, MdSpeed,
} from 'react-icons/md';
import mlOpsService from '../../services/mlOpsService';
import { ExplainButton } from './FlowSenseAIExplainer';
import { ENGINE_COLOR, fmtDate, fmtNumber, timeAgo } from './mlFormat';

const ENGINE_ICON = { SLA_GRADING: MdGavel, POTHOLE_VISION: MdCameraAlt, ROAD_CONDITION: MdAddRoad };
const REFRESH_MS = 60000;
const dash = (v, suffix = '') => (v === null || v === undefined || v === '' || Number.isNaN(v) ? '—' : `${v}${suffix}`);
const label = (s) => String(s || '').replace(/_/g, ' ');

// ── shared bits ──────────────────────────────────────────────────────────────
const Section = ({ title, icon, right, children, ...rest }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} {...rest}>
      <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
        <HStack>{icon && <Icon as={icon} color="purple.400" />}<Heading size="sm">{title}</Heading></HStack>
        {right}
      </Flex>
      {children}
    </Box>
  );
};

const Stat = ({ label: l, value, help, color = 'purple' }) => {
  const bg = useColorModeValue(`${color}.50`, 'whiteAlpha.100');
  const vc = useColorModeValue(`${color}.600`, `${color}.200`);
  return (
    <Box bg={bg} borderRadius="lg" p={3} minW={0}>
      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" noOfLines={1}>{l}</Text>
      <Text fontSize="xl" fontWeight="800" color={vc} noOfLines={1}>{value}</Text>
      {help && <Text fontSize="xs" color="gray.500" noOfLines={2}>{help}</Text>}
    </Box>
  );
};

/** Horizontal bars: [{ label, value, color?, hint? }] */
const Bars = ({ items, max, unit = '', emptyText = 'No data yet.' }) => {
  const top = max ?? Math.max(1, ...items.map(i => Number(i.value) || 0));
  if (!items.length) return <Text fontSize="sm" color="gray.500">{emptyText}</Text>;
  return (
    <VStack align="stretch" spacing={2}>
      {items.map(i => (
        <Tooltip key={i.label} label={i.hint} isDisabled={!i.hint} hasArrow placement="top-start">
          <Box>
            <Flex justify="space-between" fontSize="xs" mb={0.5}><Text noOfLines={1}>{i.label}</Text><Text fontWeight="700">{dash(i.value, unit)}</Text></Flex>
            <Progress value={top ? ((Number(i.value) || 0) / top) * 100 : 0} size="sm" colorScheme={i.color || 'purple'} borderRadius="full" />
          </Box>
        </Tooltip>
      ))}
    </VStack>
  );
};

/** 14-day stacked bars: rows [{ day, ...series }] */
const TrendBars = ({ rows, series, height = 120 }) => {
  const tokens = useToken('colors', series.map(s => s.color));
  const grid = useColorModeValue('#E2E8F0', 'rgba(255,255,255,0.08)');
  const [hover, setHover] = useState(null);
  if (!rows?.length) return <Text fontSize="sm" color="gray.500">No history yet.</Text>;
  const W = 560, H = height, pad = 18;
  const max = Math.max(1, ...rows.map(r => series.reduce((a, s) => a + (Number(r[s.key]) || 0), 0)));
  const bw = W / rows.length;
  return (
    <Box position="relative">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="14-day trend" onMouseLeave={() => setHover(null)}>
        <line x1="0" x2={W} y1={H - pad} y2={H - pad} stroke={grid} />
        {rows.map((r, i) => {
          let acc = 0;
          return (
            <g key={r.day} onMouseEnter={() => setHover(i)}>
              <rect x={i * bw} y="0" width={bw} height={H} fill="transparent" />
              {series.map((s, si) => {
                const v = Number(r[s.key]) || 0;
                if (!v) return null;
                const h = (v / max) * (H - pad - 6);
                acc += h;
                return <rect key={s.key} x={i * bw + bw * 0.18} y={H - pad - acc} width={bw * 0.64} height={Math.max(1, h)} rx="2" fill={tokens[si]} opacity={hover === null || hover === i ? 1 : 0.45} />;
              })}
              {(i % 3 === 0 || i === rows.length - 1) && <text x={i * bw + bw / 2} y={H - 4} fontSize="9" textAnchor="middle" fill="#A0AEC0">{i === rows.length - 1 ? 'today' : r.day.slice(5)}</text>}
            </g>
          );
        })}
      </svg>
      <HStack spacing={3} fontSize="xs" color="gray.500" wrap="wrap">
        {series.map((s, si) => <HStack key={s.key} spacing={1}><Box w="9px" h="9px" borderRadius="2px" bg={tokens[si]} /><Text>{s.label}</Text></HStack>)}
        {hover !== null && rows[hover] && (
          <Text fontWeight="700" color="gray.600">{rows[hover].day}: {series.map(s => `${s.label} ${rows[hover][s.key] || 0}`).join(' · ')}{rows[hover].extra ? ` · ${rows[hover].extra}` : ''}</Text>
        )}
      </HStack>
    </Box>
  );
};

const SourcesNote = ({ sources }) => {
  const entries = Object.entries(sources || {});
  if (!entries.length) return null;
  return (
    <Alert status="warning" borderRadius="lg" py={2}>
      <AlertIcon />
      <AlertDescription fontSize="xs">Some figures are unavailable: {entries.map(([k, v]) => `${label(k)} (${String(v).slice(0, 90)})`).join(' · ')}</AlertDescription>
    </Alert>
  );
};

const KeyValues = ({ obj, fmt = (v) => String(v) }) => {
  const entries = Object.entries(obj || {}).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object');
  if (!entries.length) return <Text fontSize="sm" color="gray.500">—</Text>;
  return (
    <Wrap spacing={2}>
      {entries.map(([k, v]) => <WrapItem key={k}><Badge variant="subtle" colorScheme="gray" textTransform="none" fontWeight="500">{label(k)}: <b>{fmt(v)}</b></Badge></WrapItem>)}
    </Wrap>
  );
};

// ── SLA AI ───────────────────────────────────────────────────────────────────
const GRADE_COLOR = { A: 'green', B: 'teal', C: 'yellow', D: 'orange', F: 'red' };
const SlaPanel = ({ d }) => {
  const live = d.live || {};
  const total = (live.on_track || 0) + (live.at_risk || 0) + (live.breached || 0);
  const trend = (d.trend || []).map(t => ({ ...t, extra: t.compliance !== null ? `${t.compliance}% met` : null }));
  return (
    <Stack spacing={4}>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
        <Stat label="SLA met (30 d)" value={dash(d.last30Days?.compliance_rate, '%')} help={`${dash(d.last30Days?.graded)} jobs graded`} color="green" />
        <Stat label="Average grade" value={dash(d.last30Days?.avg_score)} help="score out of 100" color="teal" />
        <Stat label="At risk now" value={dash(live.at_risk)} help={`of ${dash(live.active)} open SLA jobs`} color="orange" />
        <Stat label="Breached now" value={dash(live.breached)} color="red" />
        <Stat label="Awaiting approval" value={dash(d.pendingApproval)} help="AI decisions a supervisor must approve" color="purple" />
        <Stat label="Engine" value={d.engine?.ml_enabled && !d.engine?.ml_cooling_down ? 'Python' : 'Node mirror'} help={`${dash(d.modelVersion)} · mode ${dash(d.mode)}`} color="blue" />
      </SimpleGrid>

      {total > 0 && (
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Open SLA jobs right now</Text>
          <Flex h="14px" borderRadius="full" overflow="hidden">
            <Tooltip label={`${live.on_track} on track`} hasArrow><Box bg="green.400" w={`${(live.on_track / total) * 100}%`} /></Tooltip>
            <Tooltip label={`${live.at_risk} at risk`} hasArrow><Box bg="orange.400" w={`${(live.at_risk / total) * 100}%`} /></Tooltip>
            <Tooltip label={`${live.breached} breached`} hasArrow><Box bg="red.400" w={`${(live.breached / total) * 100}%`} /></Tooltip>
          </Flex>
        </Box>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Section title="Grades given (30 days)" icon={MdGavel}>
          <Bars items={Object.entries(d.gradeMix || {}).map(([g, v]) => ({ label: `Grade ${g}`, value: v, color: GRADE_COLOR[g] }))} />
        </Section>
        <Section title="AI decisions · last 14 days" icon={MdInsights} right={<Badge>{d.last7Days ? `7 d: ${d.last7Days.warnings} warn · ${d.last7Days.escalations} esc · ${d.last7Days.reassigned} reassign` : ''}</Badge>}>
          <TrendBars rows={trend} series={[
            { key: 'warning', label: 'warnings', color: 'yellow.400' },
            { key: 'escalate', label: 'escalations', color: 'orange.400' },
            { key: 'reassign', label: 'reassignments', color: 'purple.400' },
            { key: 'recommend', label: 'recommendations', color: 'blue.300' },
          ]} />
        </Section>
      </SimpleGrid>

      <Section title="Jobs at risk or late" icon={MdSpeed} right={<Button as={RouterLink} to="/admin/sla-ai" size="xs" rightIcon={<MdOpenInNew />} variant="ghost">SLA Command Center</Button>}>
        {d.hot?.length ? (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead><Tr><Th>Job</Th><Th>Priority</Th><Th>Assignee</Th><Th isNumeric>Time used</Th><Th isNumeric>Late</Th><Th>Projected</Th></Tr></Thead>
              <Tbody>
                {d.hot.map(h => (
                  <Tr key={h.id}>
                    <Td maxW="260px"><Text fontSize="sm" fontWeight="600" noOfLines={1}>{h.title || h.id}</Text><Text fontSize="xs" color="gray.500">{h.id}</Text></Td>
                    <Td><Badge>{dash(h.priority)}</Badge></Td>
                    <Td><Text fontSize="sm">{dash(h.assignee)}</Text></Td>
                    <Td isNumeric>{dash(h.elapsed_pct, '%')}</Td>
                    <Td isNumeric><Text color={h.minutes_late > 0 ? 'red.400' : undefined} fontWeight="700">{h.minutes_late > 0 ? `${h.minutes_late} min` : '—'}</Text></Td>
                    <Td><Badge colorScheme={GRADE_COLOR[h.projected_grade] || 'gray'}>{dash(h.projected_grade)} {h.projected_score !== undefined ? `· ${h.projected_score}` : ''}</Badge></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : <Text fontSize="sm" color="gray.500">No SLA job is at risk or late.</Text>}
      </Section>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        <Section title="Technician reliability (90 days)" icon={MdGavel}>
          {d.scoreboard?.length ? (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead><Tr><Th>#</Th><Th>Name</Th><Th isNumeric>Reliability</Th><Th isNumeric>Jobs</Th><Th isNumeric>On time</Th><Th>Grade</Th></Tr></Thead>
                <Tbody>
                  {d.scoreboard.map(r => (
                    <Tr key={`${r.rank}-${r.name}`}>
                      <Td>{r.rank}</Td>
                      <Td><Text fontSize="sm" noOfLines={1}>{r.name}</Text><Text fontSize="xs" color="gray.500">{r.type}{r.trend ? ` · ${r.trend}` : ''}</Text></Td>
                      <Td isNumeric fontWeight="700">{dash(r.reliability)}</Td>
                      <Td isNumeric>{dash(r.jobs)}</Td>
                      <Td isNumeric>{dash(r.onTimeRate, '%')}</Td>
                      <Td><Badge colorScheme={GRADE_COLOR[r.grade] || 'gray'}>{dash(r.grade)}</Badge></Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : <Text fontSize="sm" color="gray.500">No graded jobs yet — the scoreboard fills as completed jobs are graded.</Text>}
        </Section>
        <Section title="Latest AI decisions" icon={MdInsights}>
          <VStack align="stretch" spacing={2} maxH="340px" overflowY="auto">
            {(d.recentDecisions || []).map(x => (
              <Flex key={x.id} gap={2} align="flex-start">
                <Badge colorScheme={x.type === 'reassign' ? 'purple' : x.type === 'escalate' ? 'orange' : x.type === 'warning' ? 'yellow' : 'blue'} minW="78px" textAlign="center">{label(x.type)}</Badge>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" noOfLines={2}>{x.summary || `Job ${x.workOrderId}`}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {x.workOrderId} · {label(x.status)}{x.confidence !== null ? ` · confidence ${Math.round(x.confidence * 100)}%` : ''}{x.to ? ` · → ${x.to}` : ''} · {timeAgo(x.createdAt)}
                  </Text>
                </Box>
              </Flex>
            ))}
            {!d.recentDecisions?.length && <Text fontSize="sm" color="gray.500">No decisions yet.</Text>}
          </VStack>
        </Section>
      </SimpleGrid>

      <Section title="How the SLA engine is tuned" icon={MdSpeed}>
        <Stack spacing={2}>
          <Text fontSize="xs" fontWeight="700" color="gray.500">THRESHOLDS</Text>
          <KeyValues obj={d.thresholds} />
          {d.weights?.grade && (<><Text fontSize="xs" fontWeight="700" color="gray.500" mt={2}>GRADE WEIGHTS</Text><KeyValues obj={d.weights.grade} /></>)}
          {d.weights?.rank && (<><Text fontSize="xs" fontWeight="700" color="gray.500" mt={2}>REASSIGNMENT RANKING WEIGHTS</Text><KeyValues obj={d.weights.rank} /></>)}
          {d.weights?.bands && (<><Text fontSize="xs" fontWeight="700" color="gray.500" mt={2}>GRADE BANDS</Text><KeyValues obj={d.weights.bands} /></>)}
          {d.lastRun?.at && <Text fontSize="xs" color="gray.500">Last grading cycle {timeAgo(d.lastRun.at)}.</Text>}
        </Stack>
      </Section>
    </Stack>
  );
};

// ── Road damage vision ───────────────────────────────────────────────────────
const VisionPanel = ({ d }) => {
  const t = d.training || {};
  const pct01 = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 1000) / 10}%`);
  return (
    <Stack spacing={4}>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
        <Stat label="Photos collected" value={fmtNumber(d.totals?.photos)} help={Object.entries(d.totals?.bySource || {}).map(([k, v]) => `${k} ${v}`).join(' · ')} color="blue" />
        <Stat label="Labelled photos" value={fmtNumber(d.totals?.labelled)} help="confirmed or corrected by technicians" color="green" />
        <Stat label="Production model" value={dash(d.productionVersion)} help={`mAP50 ${pct01(d.productionMap50)}`} color="purple" />
        <Stat label="Answers first" value={dash(d.preferredDetector)} help="the detector techs agree with most" color="teal" />
        <Stat label="Vision engine" value={d.engine?.online ? 'Online' : 'Offline'} help={d.engine?.online ? null : d.engine?.error} color={d.engine?.online ? 'green' : 'red'} />
        <Stat label="Waiting review" value={fmtNumber(d.totals?.byStatus?.pending ?? d.totals?.byStatus?.unlabelled ?? null)} help="AI hits not yet confirmed" color="orange" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Section title="Do technicians agree with the AI?" icon={MdCameraAlt}>
          <Bars unit="%" max={100} items={(d.agreement || []).map(a => ({ label: `${a.source} (${a.agreed}/${a.reviewed})`, value: a.ratePct, color: a.ratePct >= 75 ? 'green' : a.ratePct >= 50 ? 'yellow' : 'red' }))} emptyText="No technician reviews yet." />
        </Section>
        <Section title="Labelled photos per damage type" icon={MdAddRoad}>
          <Bars items={(d.perClass || []).map(c => ({ label: label(c.label), value: c.labelled, hint: `${c.labelled} labelled of ${c.total}` }))} emptyText="No labelled photos yet." />
        </Section>
      </SimpleGrid>

      <Section title="Next automatic retrain" icon={MdRefresh} right={<Badge colorScheme={t.autopilot ? 'purple' : 'gray'}>autopilot {t.autopilot ? 'on' : 'off'}</Badge>}>
        {t.autopilotThreshold ? (
          <>
            <Progress value={t.progressPct || 0} colorScheme="purple" borderRadius="full" size="md" hasStripe />
            <Text fontSize="sm" mt={2}>{t.newLabelsSinceLastRun ?? 0} of {t.autopilotThreshold} new labels since the last run · needs at least {dash(t.minToTrain)} labelled photos to train.</Text>
          </>
        ) : <Text fontSize="sm" color="gray.500">—</Text>}
      </Section>

      <Section title="Training runs" icon={MdInsights}>
        {t.runs?.length ? (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead><Tr><Th>When</Th><Th>Version</Th><Th>Status</Th><Th isNumeric>Photos</Th><Th isNumeric>mAP50</Th><Th isNumeric>vs prod.</Th><Th>Result</Th></Tr></Thead>
              <Tbody>
                {t.runs.map(r => (
                  <Tr key={r.id}>
                    <Td><Text fontSize="sm">{fmtDate(r.startedAt)}</Text><Text fontSize="xs" color="gray.500">{dash(r.trigger)}</Text></Td>
                    <Td>{dash(r.version)}</Td>
                    <Td><Badge colorScheme={r.status === 'succeeded' ? 'green' : r.status === 'failed' ? 'red' : 'blue'}>{r.status}</Badge></Td>
                    <Td isNumeric>{dash(r.samples)}</Td>
                    <Td isNumeric>{pct01(r.map50)}</Td>
                    <Td isNumeric>{pct01(r.championMap50)}</Td>
                    <Td maxW="260px"><Tooltip label={r.reason || r.error} isDisabled={!r.reason && !r.error} hasArrow><Text fontSize="xs" noOfLines={2}>{r.promoted ? 'Promoted to production' : r.promoted === false ? 'Kept production' : r.error || '—'}</Text></Tooltip></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : <Text fontSize="sm" color="gray.500">No training runs yet.</Text>}
      </Section>

      <Section title="Photos captured · last 14 days" icon={MdCameraAlt} right={<Button as={RouterLink} to="/admin/road-works" size="xs" rightIcon={<MdOpenInNew />} variant="ghost">Road Works</Button>}>
        <TrendBars rows={d.trend} series={[{ key: 'total', label: 'photos', color: 'blue.400' }]} />
      </Section>
    </Stack>
  );
};

// ── Road condition & RUL ─────────────────────────────────────────────────────
const BAND_COLOR = { failed: 'red', poor: 'orange', fair: 'yellow', good: 'teal', excellent: 'green' };
const pciColor = (p) => (p === null || p === undefined ? 'gray' : p < 25 ? 'red' : p < 55 ? 'orange' : p < 70 ? 'yellow' : p < 85 ? 'teal' : 'green');
const RoadPanel = ({ d }) => (
  <Stack spacing={4}>
    <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
      <Stat label="Roads scored" value={`${dash(d.roads?.scored)}/${dash(d.roads?.total)}`} help={d.roads?.coveragePct !== null ? `${d.roads.coveragePct}% coverage` : null} color="blue" />
      <Stat label="Average PCI" value={dash(d.roads?.avgPci)} help="0 = failed, 100 = new" color={pciColor(d.roads?.avgPci)} />
      <Stat label="Segments scored" value={fmtNumber(d.segments?.total)} color="purple" />
      <Stat label="Average road RUL" value={dash(d.rul?.avgYears, ' y')} help={`${dash(d.rul?.roads)} roads predicted`} color="teal" />
      <Stat label="Shortest RUL" value={dash(d.rul?.minYears, ' y')} color="red" />
      <Stat label="Last scored" value={d.roads?.lastEvaluatedAt ? timeAgo(d.roads.lastEvaluatedAt) : '—'} help={Object.entries(d.engineMix || {}).map(([k, v]) => `${k} ${v}`).join(' · ') || null} color="gray" />
    </SimpleGrid>

    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
      <Section title="Segment condition (PCI bands)" icon={MdAddRoad}>
        <Bars items={(d.segments?.bands || []).map(b => ({ label: b.label, value: b.segments, color: BAND_COLOR[b.key], hint: b.pct !== null ? `${b.pct}% of scored segments` : null }))} emptyText="No segments scored yet — run “Evaluate segments” on a road." />
      </Section>
      <Section title="Recommended treatments" icon={MdInsights}>
        {Object.keys(d.actions || {}).length ? (
          <Bars items={Object.entries(d.actions).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: label(k), value: v, color: 'purple' }))} />
        ) : <Text fontSize="sm" color="gray.500">No treatments recommended yet.</Text>}
      </Section>
    </SimpleGrid>

    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
      <Section title="Worst roads by PCI" icon={MdAddRoad} right={<Button as={RouterLink} to="/admin/road-works" size="xs" rightIcon={<MdOpenInNew />} variant="ghost">Road Works</Button>}>
        {d.worstRoads?.length ? (
          <Table size="sm">
            <Thead><Tr><Th>Road</Th><Th isNumeric>PCI</Th><Th>Treatment</Th><Th>Engine</Th></Tr></Thead>
            <Tbody>
              {d.worstRoads.map(r => (
                <Tr key={r.id}>
                  <Td><Text fontSize="sm" noOfLines={1}>{r.name || r.id}</Text><Text fontSize="xs" color="gray.500">{r.evaluatedAt ? `scored ${timeAgo(r.evaluatedAt)}` : ''}</Text></Td>
                  <Td isNumeric><Badge colorScheme={pciColor(r.pci)}>{r.pci}</Badge></Td>
                  <Td><Text fontSize="xs">{label(r.action) || '—'}</Text></Td>
                  <Td><Badge variant="subtle">{dash(r.engine)}</Badge></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : <Text fontSize="sm" color="gray.500">No roads scored yet.</Text>}
      </Section>
      <Section title="Shortest remaining life (RUL)" icon={MdSpeed}>
        {d.rul?.shortest?.length ? (
          <Bars items={d.rul.shortest.map(r => ({ label: r.name, value: r.rulYears, color: r.rulYears < 2 ? 'red' : r.rulYears < 5 ? 'orange' : 'green', hint: r.confidence !== null ? `confidence ${Math.round(r.confidence * 100)}%` : null }))} unit=" y" />
        ) : <Text fontSize="sm" color="gray.500">No road RUL predictions yet.</Text>}
      </Section>
    </SimpleGrid>
  </Stack>
);

// ── main ─────────────────────────────────────────────────────────────────────
export default function EngineInsightsPanel({ overview, selected, onSelect }) {
  const engines = overview?.engines || [];
  const firstWithInsights = engines.find(e => e.hasInsights)?.key || engines[0]?.key || null;
  const key = selected && engines.some(e => e.key === selected) ? selected : firstWithInsights;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ctrl = useRef(null);

  const listBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const activeBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const load = useCallback(async ({ fresh = false, silent = false } = {}) => {
    if (!key) return;
    ctrl.current?.abort?.();
    const c = typeof AbortController !== 'undefined' ? new AbortController() : null;
    ctrl.current = c;
    if (!silent) setLoading(true);
    try {
      const d = await mlOpsService.getEngineDetail(key, { fresh, signal: c?.signal });
      if (!c?.signal?.aborted) { setDetail(d); setError(null); }
    } catch (e) {
      if (e?.name !== 'AbortError' && e?.name !== 'CanceledError' && !c?.signal?.aborted) setError(e);
    } finally {
      if (!c?.signal?.aborted) setLoading(false);
    }
  }, [key]);

  useEffect(() => { setDetail(null); load(); return () => ctrl.current?.abort?.(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') load({ silent: true }); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const current = engines.find(e => e.key === key);
  const ins = detail?.key === key ? detail.insights : null;

  return (
    <Grid templateColumns={{ base: '1fr', lg: '300px minmax(0, 1fr)' }} gap={4} alignItems="start">
      <GridItem>
        <Box bg={listBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={2}>
          <Text fontSize="xs" fontWeight="800" color={muted} textTransform="uppercase" px={2} py={1}>AI engines</Text>
          <VStack align="stretch" spacing={1}>
            {!engines.length && [0, 1, 2, 3].map(i => <Skeleton key={i} h="54px" borderRadius="lg" />)}
            {engines.map(e => (
              <Box key={e.key} as="button" type="button" textAlign="left" p={2} borderRadius="lg" bg={e.key === key ? activeBg : undefined}
                _hover={{ bg: activeBg }} onClick={() => onSelect(e.key)} aria-current={e.key === key ? 'true' : undefined}>
                <HStack justify="space-between" align="flex-start">
                  <HStack spacing={2} minW={0}>
                    <Icon as={ENGINE_ICON[e.key] || MdInsights} color="purple.400" />
                    <Text fontSize="sm" fontWeight="700" noOfLines={1}>{e.name}</Text>
                  </HStack>
                  <Badge colorScheme={ENGINE_COLOR[e.state] || 'gray'} fontSize="10px">{label(e.state)}</Badge>
                </HStack>
                <Text fontSize="xs" color={muted} noOfLines={2} mt={0.5}>{e.insight?.headline || e.detail || e.short}</Text>
              </Box>
            ))}
          </VStack>
        </Box>
      </GridItem>

      <GridItem minW={0}>
        {current && (
          <Stack spacing={4}>
            <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
              <Box minW={0}>
                <HStack>
                  <Icon as={ENGINE_ICON[current.key] || MdInsights} color="purple.400" boxSize={6} />
                  <Heading size="md">{current.name}</Heading>
                  <Badge colorScheme={ENGINE_COLOR[current.state] || 'gray'}>{label(current.state)}</Badge>
                </HStack>
                <Text fontSize="sm" color={muted} mt={1}>{current.short}</Text>
                <Text fontSize="xs" color={muted}>{current.family}{current.detail ? ` · ${current.detail}` : ''}</Text>
              </Box>
              <HStack>
                <ExplainButton topic={current.key} label="Explain" />
                <Tooltip label={detail?.generatedAt ? `Updated ${timeAgo(detail.generatedAt)}` : 'Refresh'} hasArrow>
                  <IconButton aria-label="Refresh engine data" icon={<MdRefresh />} size="sm" variant="ghost" onClick={() => load({ fresh: true })} isLoading={loading} />
                </Tooltip>
              </HStack>
            </Flex>

            {detail?.howItWorks && detail.key === key && (
              <Box fontSize="sm" color={muted} borderLeftWidth="3px" borderColor="purple.300" pl={3}>{detail.howItWorks}</Box>
            )}
            {current.usedBy?.length > 0 && (
              <Wrap spacing={2}>
                {current.usedBy.map(u => (
                  <WrapItem key={u.path + u.label}><Link as={RouterLink} to={u.path} fontSize="xs"><Badge colorScheme="purple" variant="outline" textTransform="none">{u.label} ↗</Badge></Link></WrapItem>
                ))}
              </Wrap>
            )}

            {error && !ins && (
              <Alert status="error" borderRadius="lg"><AlertIcon /><AlertDescription flex="1">Could not load this engine: {error.message}</AlertDescription><Button size="sm" onClick={() => load({ fresh: true })}>Retry</Button></Alert>
            )}
            {loading && !ins && current.hasInsights && <Stack>{[0, 1, 2].map(i => <Skeleton key={i} h="120px" borderRadius="xl" />)}</Stack>}

            {ins && ins.installed === false && (
              <Alert status="info" borderRadius="lg"><AlertIcon /><AlertDescription>{Object.values(ins.sources || {})[0] || 'This engine is not installed on this server.'}</AlertDescription></Alert>
            )}
            {ins && ins.installed !== false && <SourcesNote sources={ins.sources} />}
            {ins && ins.installed !== false && key === 'SLA_GRADING' && <SlaPanel d={ins} />}
            {ins && ins.installed !== false && key === 'POTHOLE_VISION' && <VisionPanel d={ins} />}
            {ins && ins.installed !== false && key === 'ROAD_CONDITION' && <RoadPanel d={ins} />}
            {detail && detail.key === key && !detail.hasInsights && (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <AlertDescription fontSize="sm">This engine reports its live state above; it has no extra telemetry yet. Use <b>Explain</b> or the Ask FlowSense AI tab for details.</AlertDescription>
              </Alert>
            )}
          </Stack>
        )}
        {!current && engines.length > 0 && <Text color={muted}>Select an engine.</Text>}
      </GridItem>
    </Grid>
  );
}
// --- END UPDATE
